import React, { useCallback, useEffect, useState, useMemo } from 'react';
import * as ReactVirtualized from 'react-virtualized';

import {
  Grid,
  View,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  CloseIcon,
} from '@gogame/components';
import { useProcessAsync } from '@gogame/hooks-ts';
import { ScreenLink, useScreenState } from '@gogame/screens';

import { loadSimilarConcepts } from 'scenes/Gallery/GalleryPage/Concepts/sources/fields';
import { CONCEPT_ID } from 'scenes/Gallery/GalleryPage/constants';
import { conceptPreviewLink } from 'scenes/Gallery/GalleryPage/navigation';

const SimilarConcepts = ({ name }) => {
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState(false);
  const [loading, processAsync] = useProcessAsync();
  const screenState = useScreenState();

  const loadData = useCallback(() => {
    return processAsync(loadSimilarConcepts({ name })).then((response) => {
      setData(response);
      setVisible(!!response.length);
    });
  }, [name]);

  useEffect(() => {
    if (name) {
      loadData();
    }
  }, [loadData]);

  const handleClose = useCallback(() => {
    setData([]);
    setVisible(false);
  }, []);

  const tableHeight = useMemo(() => {
    return data.length < 10 ? data.length * 30 : 300;
  }, [data.length]);

  if (!visible) {
    return null;
  }

  const rowRenderer = ({ index, style, key }) => {
    const { id, name } = data[index];
    return (
      <div style={style} key={key}>
        <ScreenLink
          data-cy={'Concept Page'}
          link={conceptPreviewLink}
          target={'_blank'}
          style={{ textDecoration: 'underline' }}
          pathParams={{
            [CONCEPT_ID]: id,
          }}
          searchParams={{
            filters: screenState.filters,
            creatives: {
              tableSettings: {
                orderDir: 'asc',
              },
            },
          }}
          noWrap
        >
          <Typography noWrap={true}>{name}</Typography>
        </ScreenLink>
      </div>
    );
  };

  return (
    <Grid item xs={12}>
      <View loading={loading}>
        <Card elevation={6}>
          <CardHeader
            title={`Similar Concepts ${data.length ? `(${data.length})` : ''}`}
            action={
              <IconButton
                icon={<CloseIcon />}
                tooltip={'Close'}
                onClick={handleClose}
              />
            }
          />
          <CardContent>
            <ReactVirtualized.List
              width={862}
              height={tableHeight}
              rowCount={data.length}
              rowHeight={30}
              rowRenderer={rowRenderer}
            />
          </CardContent>
        </Card>
      </View>
    </Grid>
  );
};

export default SimilarConcepts;
